import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import {
  LOCALSTORAGE_ACCESS_TOKEN_EXPIRES_AT,
  LOCALSTORAGE_LOGIN_TYPE,
  LOCALSTORAGE_LOGIN_TYPE_ACCOUNT,
  LOCALSTORAGE_LOGIN_TYPE_CASH,
  LOCALSTORAGE_LOGIN_TYPE_LETTER,
  LOCALSTORAGE_REFRESH_TOKEN,
  LOCALSTORAGE_REFRESH_TOKEN_EXPIRES_AT,
  LOCALSTORAGE_SESSION_EXPIRES_AT,
  LOCALSTORAGE_TOKEN,
} from './constants/login';

type TokenResponsePayload = {
  token?: string;
  refreshToken?: string;
  accessTokenExpiresAtUtc?: string;
  refreshTokenExpiresAtUtc?: string;
  sessionExpiresAtUtc?: string;
};

let refreshPromise: Promise<boolean> | null = null;

const createSessionClient = () =>
  axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

export const getStoredLoginType = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.sessionStorage.getItem(LOCALSTORAGE_LOGIN_TYPE) ?? '';
};

export const isPinLoginType = (loginType = getStoredLoginType()) =>
  loginType !== '' && loginType !== LOCALSTORAGE_LOGIN_TYPE_ACCOUNT;

export const getLoginRedirectPath = (loginType = getStoredLoginType()) => {
  switch (loginType || LOCALSTORAGE_LOGIN_TYPE_ACCOUNT) {
    case LOCALSTORAGE_LOGIN_TYPE_LETTER:
      return '/micuenta/ingresopin';
    case LOCALSTORAGE_LOGIN_TYPE_CASH:
      return '/micuenta/ingresoseparacionci';
    case LOCALSTORAGE_LOGIN_TYPE_ACCOUNT:
    default:
      return '/micuenta/ingreso';
  }
};

export const getStoredAccessToken = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const storedToken = window.sessionStorage.getItem(LOCALSTORAGE_TOKEN);
  return isValidStoredToken(storedToken) ? storedToken!.trim() : '';
};

export const isValidStoredToken = (token?: string | null) => {
  if (!token) {
    return false;
  }

  const normalizedToken = token.trim().toLowerCase();
  return normalizedToken !== '' && normalizedToken !== 'null' && normalizedToken !== 'undefined';
};

export const clearStoredPinSessionData = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(LOCALSTORAGE_TOKEN);
  window.sessionStorage.removeItem(LOCALSTORAGE_REFRESH_TOKEN);
  window.sessionStorage.removeItem(LOCALSTORAGE_ACCESS_TOKEN_EXPIRES_AT);
  window.sessionStorage.removeItem(LOCALSTORAGE_REFRESH_TOKEN_EXPIRES_AT);
  window.sessionStorage.removeItem(LOCALSTORAGE_SESSION_EXPIRES_AT);
};

export const clearStoredAuthSession = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.clear();
};

export const storePinSessionTokens = (payload: TokenResponsePayload) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (payload.token) {
    window.sessionStorage.setItem(LOCALSTORAGE_TOKEN, payload.token);
  }

  if (payload.refreshToken) {
    window.sessionStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN, payload.refreshToken);
  }

  if (payload.accessTokenExpiresAtUtc) {
    window.sessionStorage.setItem(
      LOCALSTORAGE_ACCESS_TOKEN_EXPIRES_AT,
      payload.accessTokenExpiresAtUtc
    );
  }

  if (payload.refreshTokenExpiresAtUtc) {
    window.sessionStorage.setItem(
      LOCALSTORAGE_REFRESH_TOKEN_EXPIRES_AT,
      payload.refreshTokenExpiresAtUtc
    );
  }

  if (payload.sessionExpiresAtUtc) {
    window.sessionStorage.setItem(
      LOCALSTORAGE_SESSION_EXPIRES_AT,
      payload.sessionExpiresAtUtc
    );
  }
};

export const storeAccountAccessToken = (token: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  clearStoredPinSessionData();
  window.sessionStorage.setItem(LOCALSTORAGE_TOKEN, token);
};

const parseUtcDate = (value: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const shouldRefreshPinSession = (bufferSeconds = 60) => {
  if (!isPinLoginType()) {
    return false;
  }

  const accessToken = getStoredAccessToken();
  const refreshToken = typeof window !== 'undefined'
    ? window.sessionStorage.getItem(LOCALSTORAGE_REFRESH_TOKEN)
    : null;

  if (!accessToken || !refreshToken) {
    return false;
  }

  const accessTokenExpiresAt = parseUtcDate(
    typeof window !== 'undefined'
      ? window.sessionStorage.getItem(LOCALSTORAGE_ACCESS_TOKEN_EXPIRES_AT)
      : null
  );

  if (!accessTokenExpiresAt) {
    return false;
  }

  return accessTokenExpiresAt.getTime() - Date.now() <= bufferSeconds * 1000;
};

const executeRefreshPinSession = async () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const refreshToken = window.sessionStorage.getItem(LOCALSTORAGE_REFRESH_TOKEN);
  if (!refreshToken) {
    return false;
  }

  const client = createSessionClient();
  const response = await client.post('/api/Account/RefreshToken', { refreshToken });
  storePinSessionTokens(response.data);
  return true;
};

export const refreshPinSession = async (force = false) => {
  if (!isPinLoginType()) {
    return false;
  }

  if (!force && !shouldRefreshPinSession()) {
    return true;
  }

  if (!refreshPromise) {
    refreshPromise = executeRefreshPinSession()
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

export const logoutCurrentSession = async () => {
  if (!isPinLoginType()) {
    return;
  }

  const token = getStoredAccessToken();
  if (!token) {
    return;
  }

  const client = createSessionClient();
  await client.post(
    '/api/Account/Logout',
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};
